import { Component, HostListener, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { AddressService } from '@app/services/address.service';
import { AuthServiceService } from '@app/services/auth-service.service';
import { DialogService } from '@app/services/dialog.service';
import { NotificationService } from '@app/services/notification.service';
import { ToastService } from '@app/services/toast.service';
import { ShareddtaService } from '@app/services/shareddta.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BreadcrumbService } from '@app/services/breadcrumb.service';
interface Notification {
  id: string;
  type: 'customers' | 'drivers';
  recipient: string;
  message: string;
  timestamp: string;
  timeAgo: string;
  read: boolean;
}

interface NotificationCounts {
  all: number;
  customers: number;
  drivers: number;
}
@Component({
  selector: 'app-send-notification',
  templateUrl: './send-notification.component.html',
  styleUrl: './send-notification.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class SendNotificationComponent {
  // Filter state
  currentFilter: 'all' | 'customers' | 'drivers' = 'all';
  searchQuery: string = '';
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  isLoading: boolean = true;

  // Counts
  counts: NotificationCounts = {
    all: 0,
    customers: 0,
    drivers: 0
  };

  // Notifications data
  notifications: Notification[] = [
    {
      id: '1',
      type: 'drivers',
      recipient: 'Drivers',
      message: 'Please reach on time and start delivering orders as planned.',
      timestamp: '2024-05-15T10:30:00',
      timeAgo: '8 months ago',
      read: false
    },
    {
      id: '2',
      type: 'drivers',
      recipient: 'Drivers',
      message: 'Hello Drivers, Good Evening!',
      timestamp: '2024-05-14T18:00:00',
      timeAgo: '8 months ago',
      read: true
    },
    {
      id: '3',
      type: 'customers',
      recipient: 'Customers',
      message: 'Hello Customers, Good Evening!',
      timestamp: '2024-05-14T18:00:00',
      timeAgo: '8 months ago',
      read: false
    },
    {
      id: '4',
      type: 'drivers',
      recipient: 'Drivers',
      message: 'Your next delivery is scheduled for tomorrow at 10 AM.',
      timestamp: '2025-01-02T14:20:00',
      timeAgo: '2 days ago',
      read: false
    },
    {
      id: '5',
      type: 'customers',
      recipient: 'Customers',
      message: 'Your order #12345 has been shipped and will arrive soon.',
      timestamp: '2024-12-28T09:15:00',
      timeAgo: '1 week ago',
      read: false
    }
  ];

  filteredNotifications: any;

  // Modal state
  isModalOpen: boolean = false;

  // Modal form
  recipientType: 'customers' | 'drivers' | 'all' = 'customers';
  messageText: string = '';
  selectedFiles: File[] = [];
  filePreviews: { file: File; url: string }[] = [];

  // Character count
  charCount: number = 0;
  maxChars: number = 500;

  // Menu state
  isMenuOpen: boolean = false;

  // Pagination
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;

  constructor(private router: Router, public notificationService: NotificationService, public authService: AuthServiceService, public toastr: ToastrService, private dialogService: DialogService, private toast: ToastService, private shareddta: ShareddtaService, private breadcrumbService: BreadcrumbService) { }

  ngOnInit(): void {
    this.breadcrumbService.set([
      { label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', action: () => this.router.navigate(['/admin/dashboard']) },
      { label: 'Notifications' }
    ]);
    // this.updateCounts();
    // this.filterNotifications();
    this.notificationList();

    // Set up debounced search
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(1000),
      distinctUntilChanged()
    ).subscribe(query => {
      this.currentPage = 1;
      localStorage.setItem('NotificationPage', '1');
      this.notificationList();
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  // Filter Methods
  setFilter(filter: 'all' | 'customers' | 'drivers'): void {
    this.currentFilter = filter;
    this.recipientType = this.currentFilter;
    this.notificationList();
  }

  filterNotifications(): void {
    let filtered = this.notifications;

    // Apply type filter
    if (this.currentFilter !== 'all') {
      filtered = filtered.filter(n => n.type === this.currentFilter);
    }

    // Apply search filter
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(n =>
        n.recipient.toLowerCase().includes(query) ||
        n.message.toLowerCase().includes(query)
      );
    }

    this.filteredNotifications = filtered;
    this.totalItems = filtered.length;
  }

  onSearch(query: string): void {
    this.searchQuery = query;
    this.cross = !!this.searchQuery.trim();
    this.searchSubject.next(this.searchQuery.trim());
  }

  // Update Counts
  updateCounts(): void {
    this.counts.all = this.notifications.length;
    this.counts.customers = this.notifications.filter(n => n.type === 'customers').length;
    this.counts.drivers = this.notifications.filter(n => n.type === 'drivers').length;
  }

  // Delete Notification
  deleteNotification(id: string): void {
    const confirmed = confirm('Are you sure you want to delete this notification?');
    if (confirmed) {
      this.notifications = this.notifications.filter(n => n.id !== id);
      this.updateCounts();
      this.filterNotifications();
    }
  }
  async deleteOrder(notificationId: any): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      'Delete Row',
      'Are you sure you want to delete this notification? This action cannot be undone.',
      'Delete',
      'Cancel'
    );

    if (confirmed?.confirmed) {
      const formData = new FormData();
      formData.append('ids', JSON.stringify([Number(notificationId)]))
      this.notificationService.clearAdminNotification(formData).subscribe(
        (data: any) => {
          if (this.authService.resultCodeError(data)) {
            return;
          }
          if (data.result) {
            this.toast.show('success', data.result);
            this.notificationList();
          }
        },
        (error: any) => {
          this.authService.dataError(error);
        }
      );
    }
  }
  // Clear All Notifications
  // clearAllNotifications(): void {
  //   const confirmed = confirm('Are you sure you want to clear all notifications?');
  //   if (confirmed) {
  //     this.notifications = [];
  //     this.updateCounts();
  //     this.filterNotifications();
  //     this.closeMenu();
  //   }
  // }
  async clearAllNotifications(): Promise<void> {

    let message = '';

    if (this.currentFilter === 'all') {
      message = 'Are you sure you want to delete ALL notifications?';
    } else if (this.currentFilter === 'customers') {
      message = 'Are you sure you want to delete ALL CUSTOMER notifications?';
    } else if (this.currentFilter === 'drivers') {
      message = 'Are you sure you want to delete ALL DRIVER notifications?';
    }

    const confirmed = await this.dialogService.confirm(
      'Clear Notifications',
      message,
      'Delete All',
      'Cancel'
    );

    if (confirmed?.confirmed) {

      this.notificationService
        .clearAdminNotifications(this.currentFilter)
        .subscribe(
          (data: any) => {

            if (this.authService.resultCodeError(data)) {
              return;
            }

            if (data.result) {
              this.toast.show('success', data.result);

              // Refresh list
              this.notificationList();
            }
          },
          (error: any) => {
            this.authService.dataError(error);
          }
        );
    }
  }



  // Menu Methods
  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const menuContainer = document.querySelector('.menu-container');

    if (menuContainer && !menuContainer.contains(target)) {
      this.closeMenu();
    }
  }

  // Modal Methods
  openModal(): void {
    this.isModalOpen = true;
    this.showError = false;
    this.resetForm();
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.resetForm();
  }

  resetForm(): void {
    this.messageText = '';
    this.selectedFiles = [];
    this.filePreviews = [];
    this.charCount = 0;
  }

  // Recipient Type Toggle
  toggleRecipientType(type: 'customers' | 'drivers'): void {
    this.recipientType = type;
  }

  // Character Count
  updateCharCount(): void {
    this.charCount = this.messageText.length;
  }

  getCharCountClass(): string {
    if (this.charCount >= 450) return 'limit-danger';
    if (this.charCount >= 400) return 'limit-warning';
    return '';
  }

  // File Handling
  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);

      files.forEach(file => {
        if (file.type.startsWith('image/')) {
          this.selectedFiles.push(file);

          const reader = new FileReader();
          reader.onload = (e: any) => {
            this.filePreviews.push({
              file: file,
              url: e.target.result
            });
          };
          reader.readAsDataURL(file);
        }
      });
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.filePreviews.splice(index, 1);
  }

  // Send Notification
  showError: boolean = false;
  sendNotification(): void {
    if (!this.messageText.trim()) {
      this.showError = true;
      // alert('Please enter a message');
      return;
    }
    if (!this.messageText.trim()) {
      this.showError = true;
      return;
    }
    this.showError = false;

    const formData = new FormData();

    // message
    formData.append('message', this.messageText);

    // send_to mapping
    const sendTo = this.recipientType === 'customers' ? 'Customer' : 'Driver';
    formData.append('send_to_group', sendTo);

    // images from your onFileSelect
    if (this.selectedFiles.length > 0) {
      this.selectedFiles.forEach((file: File) => {
        formData.append('notification_image', file, file.name);
      });
    }
    formData.append('title', 'Generic Notification');
    this.notificationService.sendGenericNotification(formData)
      .subscribe((res: any) => {
        if (res?.resultCode == '0') {
          this.toast.show('error', res?.errorMessage);
        } else if (res.resultDescription) {
          this.toast.show('success', res?.resultDescription || 'Notification sent successfully');
          this.closeModal();
          this.notificationList();
        }
      }, () => {
        this.toast.show('error', 'Failed to Send Notification');;
      });
    this.closeModal();
  }

  // Pagination
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
    localStorage.setItem('NotificationPage', String(this.currentPage));
    this.notificationList();
  }

  nextPage(): void {
    const totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    if (this.currentPage < totalPages) {
      this.currentPage++;
    }
    localStorage.setItem('NotificationPage', String(this.currentPage));
    this.notificationList();
  }

  getPaginationInfo(): string {
    this.currentPage = Number(localStorage.getItem('NotificationPage')) || 1
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    if (this.totalItems == 0) {
      const start = 0
      return `Showing ${start} - ${end} of ${this.totalItems} Notifications`;
    }
    else {
      return `Showing ${start} - ${end} of ${this.totalItems} Notifications`;
    }
  }


  // Close modal on ESC key
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isModalOpen) {
      this.closeModal();
    }
  }

  // Close modal on backdrop click
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.closeModal();
    }
  }
  cross: boolean = false;
  clearText() {
    this.searchQuery = '';
    this.cross = false;
    this.notificationList();
  }
  allCount:any;
  driverCount:any;
  customerCount:any;
  notificationList() {
    this.isLoading = true;
    this.notificationService.getAdminSentNotificationList(this.currentFilter, this.searchQuery, this.currentPage).subscribe(
      (data: any) => {
        if (this.authService.resultCodeError(data)) {
          this.isLoading = false;
          this.shareddta.signalPageReady();
          return;
        }
        this.filteredNotifications = data?.result?.results;
        this.totalItems = data?.result?.count;
        this.allCount =data?.result?.all_count;
        this.driverCount =data?.result?.driver_count;
        this.customerCount =data?.result?.customer_count;
        this.isLoading = false;
        this.shareddta.signalPageReady();
      },
      (error) => {
        this.isLoading = false;
        this.authService.dataError(error);
        this.shareddta.signalPageReady();
      }
    );
  }
}
